import React, { useState, useCallback, useMemo, useContext } from 'react';
import styled from 'styled-components';
import DropDown from '../../DropDown';
import CheckBox from '../../CheckBox';
import FilterLayout from '../FilterLayout';
import ConfigContext from '../../../contexts/ConfigContext';
import { FlexCenter } from '../../Flex';
import { LIVE_VIEW_FILTER_STATUSES, LiveViewStatsMap } from '../../../constants';

const StatusDot = styled.div`
  border-radius: 100%;
  width: 10px;
  height: 10px;
  margin-right: 5px;
  background-color: ${({ $color }) => $color};
  border: 1px solid ${({ theme }) => theme.colors.black};
`;

const FormattedLabelWrapper = styled(FlexCenter)`
  gap: 2px;
`;

const FormattedLabel = ({ color, label }) => (
  <FormattedLabelWrapper>
    <StatusDot $color={color} />
    {label}
  </FormattedLabelWrapper>
);

const ProjectLiveViewStatusFilter = ({
  slug,
  defaultValue,
  handleClose,
  onApply,
  search,
  onChange,
  externalOptions = {},
  hideLayout = false
}) => {
  const { t } = useContext(ConfigContext);
  const [values, setValues] = useState(defaultValue || []);

  // Which statuses this context actually offers — Check in / Check out only exist where the Live View
  // tab shows them too (useProjectShifts.js builds the list). Without a list, fall back to the statuses
  // that never depend on check in/out.
  const allowedStatuses = externalOptions[slug];

  const statusOptions = useMemo(
    () =>
      Object.entries(LIVE_VIEW_FILTER_STATUSES)
        .filter(([flag, { requiresCheckInOut }]) =>
          allowedStatuses ? allowedStatuses.some(({ id }) => id === flag) : !requiresCheckInOut
        )
        .map(([flag, { statsMapKey, labelSlug }]) => ({
          value: flag,
          label: t(labelSlug),
          color: LiveViewStatsMap[statsMapKey].color
        })),
    [allowedStatuses, t]
  );

  const handleChange = useCallback(
    (value) => {
      const newValues = values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
      setValues(newValues);
      onChange?.(newValues);
    },
    [onChange, values]
  );

  const filteredOptions = useMemo(
    () =>
      search
        ? statusOptions.filter(
            ({ label }) =>
              t('liveViewStatus').toLowerCase().includes(search.toLowerCase()) ||
              label.toLowerCase().includes(search.toLowerCase())
          )
        : statusOptions,
    [statusOptions, search, t]
  );

  if (!filteredOptions.length) {
    return null;
  }

  return (
    <FilterLayout
      name={t('liveViewStatus')}
      handleClose={handleClose}
      onApply={() => onApply(values)}
      hideLayout={hideLayout}>
      {filteredOptions.map(({ value, label, color }) => (
        <DropDown.Item key={value}>
          <CheckBox
            name={`liveViewStatus-${value}`}
            value={values.includes(value)}
            label={<FormattedLabel color={color} label={label} />}
            onChange={() => handleChange(value)}
          />
        </DropDown.Item>
      ))}
    </FilterLayout>
  );
};

export default ProjectLiveViewStatusFilter;
